import {
  PIPELINE_PATH_STAGES,
  checkMove,
  isBoardStage,
  type BoardStage,
  reasonsForStages,
  type ApplicationStage,
  type PipelineReasonOption,
  type ScreeningGuard,
  type SortingReasonOption,
} from '@nest-recruit/shared';

// One answer, for the list's buttons and the board's drag alike (F8.13 "same
// validation as button actions"): the shared rules decide, and the server decides
// again — this only chooses what to ask before sending.

/** Where a pipeline move may point: a board column, or Rejected. */
export type MoveTarget = BoardStage | 'rejected';

export function isMoveTarget(stage: ApplicationStage): stage is MoveTarget {
  return stage === 'rejected' || isBoardStage(stage);
}

export type MoveIntent =
  | { readonly kind: 'move' }
  | { readonly kind: 'reject' }
  | { readonly kind: 'skip'; readonly skipped: readonly ApplicationStage[] }
  /** D91: leaving Screening before Review, Shortlist and Contact finished — a skip of its own, with no stage names to list. */
  | { readonly kind: 'skip_screening' }
  | { readonly kind: 'refused'; readonly reason: string };

export function intentFor(
  from: ApplicationStage,
  to: ApplicationStage,
  activeStages: ReadonlySet<ApplicationStage>,
  canSkip: boolean,
  screeningGuard?: ScreeningGuard,
): MoveIntent {
  const check = checkMove(from, to, activeStages, canSkip ? 'hr_lead' : 'recruiter', screeningGuard);
  if (!check.isAllowed) return { kind: 'refused', reason: check.reason };
  if (to === 'rejected') return { kind: 'reject' };
  if (!check.isSkip) return { kind: 'move' };
  if (check.isScreeningGuard === true) return { kind: 'skip_screening' };
  const path: readonly ApplicationStage[] = PIPELINE_PATH_STAGES;
  return { kind: 'skip', skipped: path.slice(path.indexOf(from) + 1, path.indexOf(to)).filter((stage) => activeStages.has(stage)) };
}

/** The reason picker's list for these stages: each stage's own reasons, plus the `any` ones (§L19). */
export function reasonOptions(reasons: readonly PipelineReasonOption[], stages: readonly ApplicationStage[]): readonly SortingReasonOption[] {
  return reasonsForStages(reasons, stages).map(({ code, label, triggersPool, minimumContactAttempts }) => ({ code, label, triggersPool, minimumContactAttempts }));
}

/**
 * The picker greys out a reason needing logged contact attempts it cannot count; from
 * the pipeline the server counts them per person and names anyone short, so every
 * reason is offered here.
 */
export const CONTACT_ATTEMPTS_CHECKED_BY_SERVER = Number.MAX_SAFE_INTEGER;
